import { lazy, ReactElement, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { checkIfTokenExpired } from '@/utils/Auth';
import { ErrorMessage } from '@/utils/Messages';
import { checkKeyMainRoute, getMainRoute } from '@/utils/Routes';
import { walletHandoffUrl } from '@/utils/Wallet/handoff';

import { FinancialClosePropertyProvider } from '@/context/FinancialClosePage/FinancialClosePropertyContext/FinancialClosePropertyContext';
import { useIdpAuth } from '@/context/IdpAuth/IdpAuthContext';
import { InsertPropertyCategoryLocationProvider } from '@/context/InsertPropertyCategoryLocationContext/InsertPropertyCategoryLocationContext';
import { OnboardingHandoverProvider } from '@/context/OnboardingContext/OnboardingHandoverContext';
import { OwnerExtractProvider } from '@/context/OwnerPage/OwnerExtractContext/OwnerExtractContext';
import { OwnerServicesTermsProvider } from '@/context/OwnerPage/OwnerServicesTermsContext/OwnerServicesTermsContext';
import { OwnerTransferControlProvider } from '@/context/OwnerPage/OwnerTransferControlContext/OwnerTransferControlContext';
import { useToast } from '@/context/ToastContext';
import { useUser } from '@/context/UserContext';
import { useViewMode } from '@/context/ViewMode/ViewMode';

import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import DefaultLayout from '@/layouts/DefaultLayout/DefaultLayout';
import OwnerLayout from '@/layouts/OwnerLayout/OwnerLayout';

import AuthCallback from '@/pages/AuthCallback';
import { CentralManutencaoGate } from '@/pages/CentralManutencao/CentralManutencaoGate';
import Home from '@/pages/Home';
import { FavoriteActivitiesProvider } from '@/pages/Home/context/FavoriteActivitiesContext';
import Login from '@/pages/Login';
import PortaoDaJornada from '@/pages/Partners/components/PortaoDaJornada';
import RedirecionaParaHostzone from '@/pages/Partners/components/RedirecionaParaHostzone';
import { partnersRoutes } from '@/pages/Partners/types/paths';
import SemConta from '@/pages/SemConta';

import { CentralAtividadesGate } from '@/components/AtendimentoFranquias/CentralAtividadesGate';
import { ChannelManagerGate } from '@/components/ChannelManager/ChannelManagerGate';
import Loader from '@/components/Loader';

import { LaunchBOContextProvider } from './context/LaunchBOProvider/LaunchBOProvider';
import GuestDamageFranchisePage from './pages/GuestDamageFranchise';
import { AmenitiesProvider } from './pages/PropertyAmenities/contexts/AmenitiesContext';

const Controller = lazy(() => import('@/pages/Controller'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ExpensesV2 = lazy(() => import('@/pages/ExpensesV2'));
const HostProperties = lazy(() => import('@/pages/HostProperties'));
const HostTedList = lazy(() => import('@/pages/HostTedList'));
const HostPerspectiveDashboard = lazy(
  () => import('@/pages/HostPerspectiveDashboard/HostPerspectiveDashboard'),
);
const TaskManagement = lazy(() => import('@/pages/TaskManagement'));
const CentralAtividadesScope = lazy(
  () => import('@/pages/AtendimentoFranquias/CentralAtividadesScope'),
);
const CentralManutencao = lazy(() => import('@/pages/CentralManutencao'));
const CentralChurn = lazy(() => import('@/pages/CentralChurn'));
const MigracoesFranquia = lazy(() => import('@/pages/MigracoesFranquia'));
const MigracaoDetalhe = lazy(() => import('@/pages/MigracoesFranquia/MigracaoDetalhe'));
const ChannelManagerHome = lazy(() => import('@/pages/ChannelManager'));
const AirbnbChannel = lazy(() => import('@/pages/ChannelManager/channels/airbnb/AirbnbChannel'));
const AirbnbAuthorization = lazy(
  () => import('@/pages/ChannelManager/channels/airbnb/account/AirbnbAuthorization'),
);
const AirbnbInbox = lazy(() => import('@/pages/ChannelManager/channels/airbnb/inbox/InboxPage'));
const CmPropertyConfig = lazy(() => import('@/pages/ChannelManager/property/PropertyConfig'));
const CmReviews = lazy(() => import('@/pages/ChannelManager/reviews/ReviewsPage'));
const PartnerCommissionManagement = lazy(() => import('@/pages/PartnerCommissionManagement'));
const PartnerWithdrawRequests = lazy(() => import('@/pages/PartnerWithdrawRequests'));
const ApproveInvestorList = lazy(() => import('@/pages/ApproveInvestorList'));
const PropertyCodeGenerator = lazy(() => import('@/pages/PropertyCodeGenerator'));
const SimulacaoEscolhaFranqueado = lazy(() => import('@/pages/SimulacaoEscolhaFranqueado'));
const UploadConciliation = lazy(() => import('@/pages/UploadConciliation'));
const ConciliationHistory = lazy(() => import('@/pages/UploadConciliation/History'));
const LinkPage = lazy(() => import('@/pages/LinkPage'));
const NfList = lazy(() => import('@/pages/NfList'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const OwnerTedList = lazy(() => import('@/pages/OwnerTedList'));
const Owners = lazy(() => import('@/pages/Owners'));
const PaymentVoucher = lazy(() => import('@/pages/PaymentVoucher'));
const CompReservation = lazy(() => import('@/pages/CompReservation'));
const SendingVouchers = lazy(() => import('@/pages/SendingVouchers'));
const HostClosePage = lazy(() => import('@/pages/FinancialClose/HostClosePage/HostClosePage'));
const PropertyClosePage = lazy(
  () => import('@/pages/FinancialClose/PropertyClosePage/PropertyClosePage'),
);
const InsertAddListingDataPage = lazy(
  () => import('@/pages/Listing/InsertAddListingDataPage/InsertAddListingDataPage'),
);
const InsertChangePropertyHostDataPage = lazy(
  () =>
    import('@/pages/InsertData/InsertChangePropertyHostDataPage/InsertChangePropertyHostDataPage'),
);

const InsertChangePropertyStatusPage = lazy(
  () => import('@/pages/InsertData/InsertChangePropertyStatusPage'),
);
const EditPropertyOperationalPage = lazy(
  () => import('@/pages/EditPropertyOperational/EditPropertyOperationalPage'),
);
const InsertHostDataPage = lazy(
  () => import('@/pages/InsertData/InsertHostDataPage/InsertHostDataPage'),
);
const InsertOwnerDataPage = lazy(
  () => import('@/pages/InsertData/InsertOwnerDataPage/InsertOwnerDataPage'),
);
const InsertPropertyAmenitiesDataPage = lazy(
  () => import('@/pages/InsertData/InsertPropertyAmenitiesData/InsertPropertyAmenitiesDataPage'),
);
const InsertPropertyDataPage = lazy(
  () => import('@/pages/InsertData/InsertPropertyDataPage/InsertPropertyDataPage'),
);
const InsertPropertyRulesDataPage = lazy(
  () => import('@/pages/InsertData/InsertPropertyRulesDataPage/InsertPropertyRulesDataPage'),
);
const ListingStatusPage = lazy(() => import('@/pages/Listing/ListingStatusPage'));
const OwnerPropertyDetails = lazy(() => import('@/pages/Owners/PropertyDetailsOwner'));
const PersonalDataHost = lazy(() => import('@/pages/PersonalDataHostPage'));
const PersonalDataOwnerPage = lazy(() => import('@/pages/PersonalDataOwnerPage'));
const ViewHosts = lazy(() => import('@/pages/ViewHosts'));
const ViewOwners = lazy(() => import('@/pages/ViewOwners'));
const ManagementPanelPage = lazy(() => import('@/pages/ManagementPanel/ManagementPanelPage'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const NewPasswordPage = lazy(() => import('@/pages/NewPasswordPage'));
const GuestDamagePage = lazy(() => import('@/pages/GuestDamage/GuestDamagePage'));
const GuestDamageV2 = lazy(() => import('@/pages/GuestDamageV2/GuestDamageV2'));
const EasyCover = lazy(() => import('@/pages/EasyCover'));
const EasyCoverDashboard = lazy(() =>
  import('@/pages/EasyCover').then((m) => ({ default: m.EasyCoverDashboard })),
);
const FinishedDamages = lazy(() => import('@/pages/FinishedDamages/FinishedDamages'));
const DetailsDamage = lazy(() => import('@/pages/DetailsDamage/DetailsDamage'));
const NovoDano = lazy(() => import('@/pages/NovoDano/NovoDano'));
const CriarDano = lazy(() => import('@/pages/CriarDano/CriarDano'));
const OwnerPerspectiveDashboard = lazy(
  () => import('@/pages/OwnerPerspectiveDashboard/OwnerPerspectiveDashboard'),
);
const PartnerPerspectiveDashboard = lazy(
  () => import('@/pages/PartnerPerspectiveDashboard/PartnerPerspectiveDashboard'),
);
const HostPerspectiveFranquiasDashboard = lazy(
  () => import('@/pages/HostPerspectiveFranquiasDashboard/HostPerspectiveFranquiasDashboard'),
);
const ExecutivesPage = lazy(() => import('@/pages/Executives'));
const HostFranchiseOperationsPage = lazy(
  () => import('@/pages/HostFranchiseOperations/HostFranchiseOperationsPage'),
);
const TimeLine = lazy(() => import('@/pages/TimeLine/TimeLine'));
const PropertyAmenities = lazy(() => import('@/pages/PropertyAmenities'));
const PropertyAmenitiesDetails = lazy(() => import('@/pages/PropertyAmenitiesDetails'));

// Lazy-loaded PreCheckin pages
const PreCheckin = lazy(() => import('@/pages/PreCheckin'));
const PreCheckinMainPage = lazy(() =>
  import('@/pages/PreCheckin').then((module) => ({ default: module.PreCheckinMainPage })),
);
const PreCheckinFinishPage = lazy(() =>
  import('@/pages/PreCheckin').then((module) => ({ default: module.PreCheckinFinishPage })),
);
const PreCheckinNotFoundPage = lazy(() =>
  import('@/pages/PreCheckin').then((module) => ({ default: module.PreCheckinNotFoundPage })),
);
const PreCheckinForgotToken = lazy(() =>
  import('@/pages/PreCheckin').then((module) => ({ default: module.PreCheckinForgotToken })),
);

// // Lazy-loaded Partners pages
const Partners = lazy(() => import('@/pages/Partners/Partners'));
const PartnersPublicWrapper = lazy(() => import('@/pages/Partners/PartnersPublicWrapper'));
const PartnersEarningsPage = lazy(() => import('@/pages/Partners/pages/Earnings/Earnings'));
const PartnersEarningsRescuePage = lazy(
  () => import('@/pages/Partners/pages/Earnings/Rescue/Rescue'),
);
const PartnersIndicatePage = lazy(() => import('@/pages/Partners/pages/Indicate/Indicate'));
const PartnersIndicatePageForm = lazy(() => import('@/pages/Partners/pages/Indicate/Form/Form'));
const PartnersParceiroConviteEnviado = lazy(
  () => import('@/pages/Partners/pages/Indicate/ParceiroFechar/ConviteEnviado'),
);
const PartnersLocationEntry = lazy(
  () => import('@/pages/Partners/pages/Indicate/LocationEntry/LocationEntry'),
);
const PartnersLocationWizard = lazy(() => import('@/pages/Partners/pages/Indicate/Wizard'));
const PartnersSpotChoice = lazy(() => import('@/pages/Partners/pages/Indicate/SpotChoice'));
const PartnersBulkSpotIndicationForm = lazy(
  () => import('@/pages/Partners/pages/Indicate/Form/Content/BulkSpotIndicationForm'),
);
const PartnersIndicationsPage = lazy(
  () => import('@/pages/Partners/pages/Indications/Indications'),
);
const PartnersIndicationsViewsContentPage = lazy(
  () => import('@/pages/Partners/pages/Indications/IndicateViews/Content/Content'),
);
const PartnersIndicationDetailPage = lazy(
  () => import('@/pages/Partners/pages/Indications/Detail'),
);
const PartnersContractDataPage = lazy(
  () => import('@/pages/Partners/pages/Indications/ContractData'),
);
const PartnersSsoEntry = lazy(() => import('@/pages/Partners/pages/SsoEntry/SsoEntry'));

/**
 * Prévia da etapa 3 com dados de exemplo — só em DEV.
 *
 * `import.meta.env.DEV` é constante no build de produção, então o Rollup elimina
 * a rota E o chunk: nada disso chega ao bundle publicado. Ver o cabeçalho de
 * `ContractData/Previa.tsx` para por que a tela real não abre localmente.
 */
const PartnersContractDataPreviaPage = lazy(
  () => import('@/pages/Partners/pages/Indications/ContractData/Previa'),
);
const LaunchBOPage = lazy(() =>
  import('@/pages/LaunchBO').then((module) => ({ default: module.LaunchBOPage })),
);
const LaunchBOViewContentPage = lazy(() =>
  import('@/pages/LaunchBO').then((module) => ({
    default: module.LaunchBOViewContentPage,
  })),
);
const PartnersPanelPage = lazy(() => import('@/pages/Partners/pages/Panel/Panel'));
const PartnersUsefulLinksPage = lazy(
  () => import('@/pages/Partners/pages/UsefulLinks/UsefulLinks'),
);
const PartnersLancamentosPage = lazy(
  () => import('@/pages/Partners/pages/Lancamentos/Lancamentos'),
);
const PartnersLancamentosDetailPage = lazy(
  () => import('@/pages/Partners/pages/Lancamentos/Detail'),
);
const PartnersRevendasPage = lazy(() => import('@/pages/Partners/pages/Revendas/Revendas'));
const PartnersRevendasDetailPage = lazy(() => import('@/pages/Partners/pages/Revendas/Detail'));
const PartnersMapAllotmentPage = lazy(() => import('@/pages/Partners/pages/MapAllotment'));
const PartnersQuestionsPage = lazy(() => import('@/pages/Partners/pages/Questions/Questions'));
const PartnersEspelhoVendasPage = lazy(
  () => import('@/pages/Partners/pages/EspelhoVendas/EspelhoVendas'),
);

const PartnersWelcomePage = lazy(() => import('@/pages/Partners/pages/Welcome/Welcome'));
const PartnerLocationIndicationForm = lazy(() => import('@/pages/PartnerLocationIndicationForm'));
const PartnersSpotmatchListPage = lazy(() => import('@/pages/Partners/pages/Spotmatch/List'));
const PartnersSpotmatchWizardPage = lazy(() => import('@/pages/Partners/pages/Spotmatch/Wizard'));
const PartnersSpotmatchDetailPage = lazy(() => import('@/pages/Partners/pages/Spotmatch/Detail'));
const PartnerLocationIndicationDetailPage = lazy(
  () => import('@/pages/PartnerLocationIndicationDetail'),
);
const EditOrganizationPage = lazy(() => import('@/pages/EditOrganization'));
const GeoMap = lazy(() => import('@/pages/Geo'));
const GeoPolygons = lazy(() => import('@/pages/Geo/PolygonAdminPage'));
const GeoExpansionRequests = lazy(() => import('@/pages/Geo/ExpansionRequestsPage'));
const GeoExpansionRequestDetail = lazy(() => import('@/pages/Geo/ExpansionRequestDetailPage'));
// Lazy-loaded components
const InsertB2BCondominium = lazy(() => import('@/components/InsertData/InsertAddB2BCondominium'));
const InsertAddPartnerData = lazy(() => import('@/components/InsertData/InsertAddPartnerData'));

type RequireAuthProps = {
  children: ReactElement;
  permissions: string[];
  verifyHostOps?: boolean;
};

const RequireAuth = ({ children, permissions, verifyHostOps = false }: RequireAuthProps) => {
  const toast = useToast();
  const location = useLocation();
  const { user, userInformation, propertiesIdsOwnerLogged, clearSession } = useUser();
  const { status: idpStatus } = useIdpAuth();
  const roles = userInformation?.roles || [];
  const propertiesOwner = propertiesIdsOwnerLogged || [];
  const walletUrl = import.meta.env.REACT_APP_PUBLIC_WALLET_URL;

  const isTokenExpired = checkIfTokenExpired();
  if (isTokenExpired) {
    toast.error(ErrorMessage.expiredToken());
    window.localStorage.removeItem('sapron-pms-user');
  }

  if (userInformation && (roles.includes('Owner') || roles.includes('Admin'))) {
    const currentPath = window.location.pathname;
    const ownerSpecificRoutes = ['/proprietario', '/meusdados'];
    const isAccessingOwnerRoute = ownerSpecificRoutes.some(
      (route) => currentPath === route || currentPath.startsWith(`${route}/`),
    );

    if (isAccessingOwnerRoute) {
      // Sessão do IDP não repassa token: a Wallet entra pela mesma sessão SSO
      // do Keycloak (WAL-F01 aposentado). Só a sessão legada ainda precisa do
      // handoff por URL, e ele sai junto com o login por senha em `idp-only`.
      const destino = walletHandoffUrl({
        walletUrl,
        comSessaoDoIdp: idpStatus === 'authenticated',
        accessToken: user?.access,
      });
      // Sessão legada: `clearSession`, não `logout` — encerrar a SSO aqui
      // derrubaria a Wallet no mesmo movimento que abre ela. Sessão do IDP não
      // é limpa: os tokens só vivem em memória e morrem com a saída da página,
      // e limpar antes fazia o layout do proprietário e os popups chamarem a
      // API sem token (os `401` do redirecionamento).
      if (idpStatus !== 'authenticated') clearSession();
      window.location.href = destino;
      return null;
    }
  }

  let userRedirectTo = '/login';

  function getAuth() {
    if (
      !user ||
      !userInformation ||
      isTokenExpired ||
      roles.length === 0 ||
      permissions.length === 0
    ) {
      return false;
    }
    let userHasPermission = !!permissions.find((role) => roles.includes(role));
    const isOwnerAndHaveProperties = roles.includes('Owner') && propertiesOwner.length > 0;
    if (isOwnerAndHaveProperties) {
      const url = window.location.pathname.trim();
      const splittedUrl = url.split('/') || [];
      if (splittedUrl.length >= 3 && !splittedUrl.includes('parceiros')) {
        userHasPermission = propertiesOwner.includes(Number(splittedUrl[2]));
      }
    }
    const isHostOps = roles.includes('Host') && userInformation.host?.is_host_ops === true;
    if (userHasPermission) {
      if (isHostOps && verifyHostOps) {
        userRedirectTo = getMainRoute[checkKeyMainRoute(userInformation)];
        return false;
      }
      return true;
    }
    if (userInformation) {
      userRedirectTo = getMainRoute[checkKeyMainRoute(userInformation)];
    }

    return false;
  }

  const isUserAuthenticated = getAuth();

  // Preserva o destino (path + querystring) ao mandar pro login, para o
  // pós-login voltar pra onde o usuário queria ir (deep-links, ex.:
  // /geo?polygon=<id> vindo do Sonar). O caso "logado sem permissão"
  // continua indo pra rota principal do role, sem state.
  //
  // Exceção: a rota '/' é um hub de redirect (RequireAuth permissions={[]}
  // sempre reprova em getAuth), nunca um destino real. Guardá-la como `from`
  // faz o pós-login navegar pra '/', que reprova de novo e volta pro /login
  // num loop. O logout() no mount do Login ainda derruba a sessão recém-criada.
  const shouldPreserveFrom = userRedirectTo === '/login' && location.pathname !== '/';
  return isUserAuthenticated ? (
    children
  ) : (
    <Navigate to={userRedirectTo} state={shouldPreserveFrom ? { from: location } : undefined} />
  );
};

RequireAuth.defaultProps = {
  verifyHostOps: false,
};

const RequireAccountOwner = ({ children }: { children: ReactElement }) => {
  const { userInformation } = useUser();
  if (userInformation?.partner?.is_account_owner === false) {
    return <Navigate to={`/${partnersRoutes.partners.main}/${partnersRoutes.panel.main}`} />;
  }
  return children;
};

const InicialRedirect = () => {
  const { userInformation } = useUser();
  let userRedirecTo = '/login';
  if (userInformation) {
    userRedirecTo = getMainRoute[checkKeyMainRoute(userInformation)];
  }

  return <Navigate to={userRedirecTo} />;
};
const Router = () => {
  const { userInformation } = useUser();
  const roles = userInformation?.roles || [];
  const { isViewMode } = useViewMode();
  const viewModeHostPermission = isViewMode ? ['SeazoneAdministrative', 'SeazoneOnboarding'] : [''];
  const viewModeOwnerPermission = isViewMode
    ? ['SeazoneAdministrative', 'SeazoneOnboarding']
    : [''];

  const { featureFlags } = useFeatureFlags();

  const isNewDamagePageEnabled = featureFlags.ff_enable_franchise_new_damage_v2;

  const FinancialCloseLayout = () => (
    <FinancialClosePropertyProvider>
      <Outlet />
    </FinancialClosePropertyProvider>
  );

  return (
    <Suspense fallback={<Loader show />}>
      <Routes>
        <Route path="/" element={<DefaultLayout />}>
          <Route
            path="/"
            element={
              <RequireAuth permissions={[]}>
                <InicialRedirect />
              </RequireAuth>
            }
          />
          <Route
            path="/home"
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                  'Attendant',
                  'Host',
                ]}
              >
                <FavoriteActivitiesProvider>
                  <Home />
                </FavoriteActivitiesProvider>
              </RequireAuth>
            }
          />
          <Route
            path="/painel-perspectiva-do-proprietario"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <OwnerPerspectiveDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/painel-perspectiva-do-parceiro"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <PartnerPerspectiveDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/painel-perspectiva-do-franqueado"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <HostPerspectiveFranquiasDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/gerenciar-executivos"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <ExecutivesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/gestao-operacoes-franquia"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <HostFranchiseOperationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/multicalendar"
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'SeazoneAdministrative',
                  'Attendant',
                  'Host',
                  ...viewModeHostPermission,
                ]}
              >
                <TimeLine />
              </RequireAuth>
            }
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Host', ...viewModeHostPermission]}>
                <PersonalDataHost />
              </RequireAuth>
            }
            path="/minhasinformacoes"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Attendant']}>
                <CompReservation />
              </RequireAuth>
            }
            path="/compreservas"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Attendant']}>
                <SendingVouchers />
              </RequireAuth>
            }
            path="/vouchersenviados"
          />
          <Route
            path="/lancamento-bo"
            element={
              <RequireAuth
                permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
              >
                <LaunchBOContextProvider>
                  <LaunchBOPage />
                </LaunchBOContextProvider>
              </RequireAuth>
            }
          >
            <Route index element={<LaunchBOViewContentPage />} />
          </Route>
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Attendant', 'SeazoneAdministrative']}>
                <PaymentVoucher />
              </RequireAuth>
            }
            path="/comprovantespagamento"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Host', ...viewModeHostPermission]}>
                <Controller />
              </RequireAuth>
            }
            path="/controle"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Host', ...viewModeHostPermission]} verifyHostOps>
                <Dashboard />
              </RequireAuth>
            }
            path="/dashboard"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'SeazoneOnboarding',
                  'SeazoneAdministrative',
                  'Host',
                  ...viewModeHostPermission,
                ]}
                verifyHostOps={false}
              >
                <ExpensesV2 />
              </RequireAuth>
            }
            path="/despesas-v2"
          />
          <Route
            element={
              <RequireAuth
                permissions={['Admin', 'SeazoneOnboarding', 'SeazoneAdministrative']}
                verifyHostOps
              >
                <ExpensesV2 />
              </RequireAuth>
            }
            path="/despesas"
          />
          <Route
            element={
              <RequireAuth
                permissions={['Host', 'SeazoneAdministrative', 'Admin', ...viewModeHostPermission]}
              >
                <ExpensesV2 />
              </RequireAuth>
            }
            path="/reembolso"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Host', ...viewModeHostPermission]}>
                <LinkPage />
              </RequireAuth>
            }
            path="/links"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <HostClosePage />
              </RequireAuth>
            }
            path="/fechamentoanfitriao"
          />
          <Route element={<FinancialCloseLayout />}>
            <Route
              path="/fechamentoimovel"
              element={
                <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                  <PropertyClosePage />
                </RequireAuth>
              }
            />
            <Route
              element={
                <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                  <UploadConciliation />
                </RequireAuth>
              }
              path="/conciliacao"
            />
            <Route
              element={
                <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                  <UploadConciliation />
                </RequireAuth>
              }
              path="/conciliacao"
            />
            <Route
              element={
                <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                  <ConciliationHistory />
                </RequireAuth>
              }
              path="/historicoconciliacao"
            />
          </Route>
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <OwnerTedList />
              </RequireAuth>
            }
            path="/listaderepassesproprietario"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <HostTedList />
              </RequireAuth>
            }
            path="/listaderepassesanfitriao"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <NfList />
              </RequireAuth>
            }
            path="/listadenfs"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Host',
                  'SeazoneOnboarding',
                  'Attendant',
                  ...viewModeHostPermission,
                ]}
              >
                <HostProperties />
              </RequireAuth>
            }
            path="/propriedades"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <OnboardingHandoverProvider>
                  <InsertPropertyCategoryLocationProvider>
                    <Onboarding />
                  </InsertPropertyCategoryLocationProvider>
                </OnboardingHandoverProvider>
              </RequireAuth>
            }
            path="/onboarding"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertB2BCondominium />
              </RequireAuth>
            }
            path="/inserirdados/condominio-b2b"
          />

          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <AmenitiesProvider>
                  <PropertyAmenities />
                </AmenitiesProvider>
              </RequireAuth>
            }
            path="/inserirdados/property-amenities"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <AmenitiesProvider>
                  <PropertyAmenitiesDetails />
                </AmenitiesProvider>
              </RequireAuth>
            }
            path="/inserirdados/property-amenities/:propertyCode"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <PropertyAmenities />
              </RequireAuth>
            }
            path="/inserirdados/property-amenities-design"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <ViewOwners />
              </RequireAuth>
            }
            path="/proprietarios"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <ViewHosts />
              </RequireAuth>
            }
            path="/anfitrioes"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertAddPartnerData />
              </RequireAuth>
            }
            path="/inserirdados/parceiro"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertAddPartnerData />
              </RequireAuth>
            }
            path="/editardados/parceiro"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertHostDataPage />
              </RequireAuth>
            }
            path="/inserirdados/anfitriao"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertPropertyAmenitiesDataPage />
              </RequireAuth>
            }
            path="/inserirdados/comodidades"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertPropertyRulesDataPage />
              </RequireAuth>
            }
            path="/inserirdados/regras"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertAddListingDataPage />
              </RequireAuth>
            }
            path="/inserirdados/novo-listing"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <ListingStatusPage />
              </RequireAuth>
            }
            path="/statusdosanuncios"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertChangePropertyStatusPage />
              </RequireAuth>
            }
            path="/inserirdados/mudar-status-imovel"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding', ...viewModeOwnerPermission]}>
                <InsertPropertyDataPage />
              </RequireAuth>
            }
            path="/editardados/propriedade"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'Host']}>
                <EditPropertyOperationalPage />
              </RequireAuth>
            }
            path="/editardados/propriedade/franquia"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertHostDataPage />
              </RequireAuth>
            }
            path="/editardados/anfitriao"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertChangePropertyHostDataPage />
              </RequireAuth>
            }
            path="/editardados/mudar-anfitriao"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <InsertOwnerDataPage />
              </RequireAuth>
            }
            path="/editardados/proprietario"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding', ...viewModeOwnerPermission]}>
                <InsertAddListingDataPage />
              </RequireAuth>
            }
            path="/editardados/listing"
          />
          <Route
            element={
              <RequireAuth
                permissions={['Admin', 'SeazoneOnboarding', 'SeazoneAdministrative', 'Attendant']}
              >
                <ManagementPanelPage />
              </RequireAuth>
            }
            path="/painelgerenciamento"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                  ...viewModeHostPermission,
                ]}
              >
                {!roles.includes('Host') && !isViewMode ? (
                  <GuestDamageV2 />
                ) : isNewDamagePageEnabled ? (
                  <GuestDamageFranchisePage />
                ) : (
                  <GuestDamagePage />
                )}
              </RequireAuth>
            }
            path="/danosdehospede"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                  ...viewModeHostPermission,
                ]}
              >
                {!roles.includes('Host') && !isViewMode ? <GuestDamageV2 /> : <GuestDamagePage />}
              </RequireAuth>
            }
            path="/danosdehospede/:id"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <EasyCover />
              </RequireAuth>
            }
            path="/easy-cover"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <EasyCoverDashboard />
              </RequireAuth>
            }
            path="/easy-cover/dashboard"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <HostPerspectiveDashboard />
              </RequireAuth>
            }
            path="/painel-perspectiva-do-anfitriao"
          />

          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                  ...viewModeHostPermission,
                ]}
              >
                <FinishedDamages />
              </RequireAuth>
            }
            path="/danosdehospede/finalizados"
          />

          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                ]}
              >
                <GuestDamageV2 />
              </RequireAuth>
            }
            path="/danosdehospede-v2"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                ]}
              >
                <GuestDamageV2 />
              </RequireAuth>
            }
            path="/danosdehospede-v2/:id"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                ]}
              >
                <DetailsDamage />
              </RequireAuth>
            }
            path="/detalhes-dano/:id"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <NovoDano />
              </RequireAuth>
            }
            path="/novo-dano"
          />
          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                ]}
              >
                <CriarDano />
              </RequireAuth>
            }
            path="/criar-dano"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOnboarding']}>
                <HostPerspectiveDashboard />
              </RequireAuth>
            }
            path="/painel-perspectiva-do-anfitriao"
          />

          <Route
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Attendant',
                  'Host',
                  'SeazoneAdministrative',
                  'SeazoneOnboarding',
                ]}
              >
                <TaskManagement />
              </RequireAuth>
            }
            path="/gestaodetarefas"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <PartnerCommissionManagement />
              </RequireAuth>
            }
            path="/gestao-comissoes-parceiros"
          />

          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <PartnerWithdrawRequests />
              </RequireAuth>
            }
            path="/solicitacoes-resgate-parceiro"
          />

          <Route
            element={
              <RequireAuth
                permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
              >
                <CentralAtividadesGate>
                  <CentralAtividadesScope />
                </CentralAtividadesGate>
              </RequireAuth>
            }
            path="/central-atividades/*"
          />
          <Route
            element={
              <RequireAuth
                permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
              >
                <CentralManutencaoGate>
                  <CentralManutencao />
                </CentralManutencaoGate>
              </RequireAuth>
            }
            path="/central-manutencao"
          />
          <Route
            element={
              <RequireAuth
                permissions={['Admin', 'Attendant', 'SeazoneAdministrative', 'SeazoneOnboarding']}
              >
                <CentralChurn />
              </RequireAuth>
            }
            path="/central-churn/:tab?"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <MigracoesFranquia />
              </RequireAuth>
            }
            path="/migracoes"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative', 'SeazoneOnboarding']}>
                <MigracaoDetalhe />
              </RequireAuth>
            }
            path="/migracoes/:id"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <ApproveInvestorList />
              </RequireAuth>
            }
            path="/admin/aprovar-lista"
          />
          <Route
            element={
              <RequireAuth permissions={['Admin', 'SeazoneAdministrative']}>
                <PropertyCodeGenerator />
              </RequireAuth>
            }
            path="/admin/property-code-generator"
          />
          <Route
            element={
              <RequireAuth
                permissions={['Admin', 'Seazone', 'SeazoneOnboarding', 'SeazoneAdministrative']}
              >
                <SimulacaoEscolhaFranqueado />
              </RequireAuth>
            }
            path="/admin/simulacao-escolha-franqueado"
          />
          <Route
            path="/geo"
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Seazone',
                  'SeazoneOnboarding',
                  'SeazoneAdministrative',
                  'SeazoneMarketing',
                  'SeazoneOTA',
                  'SeazonePricing',
                  'SeazoneSysAdmin',
                ]}
              >
                <GeoMap />
              </RequireAuth>
            }
          />
          <Route
            path="/geo/polygons"
            element={
              <RequireAuth permissions={['Admin']}>
                <GeoPolygons />
              </RequireAuth>
            }
          />
          {/* Fila de pedidos de ampliação (menu "Pedidos de ampliação", irmão
              de "Mapa"). Mesma lista de `/geo`: o backend libera
              `IsAdmin | IsSeazone`. Declarada antes do detalhe `:id`. */}
          <Route
            path="/geo/expansion-requests"
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Seazone',
                  'SeazoneOnboarding',
                  'SeazoneAdministrative',
                  'SeazoneMarketing',
                  'SeazoneOTA',
                  'SeazonePricing',
                  'SeazoneSysAdmin',
                ]}
              >
                <GeoExpansionRequests />
              </RequireAuth>
            }
          />
          {/* Detalhe do pedido de ampliação (link do Slack cai aqui). Mesma
              lista de `/geo`: o backend libera `IsAdmin | IsSeazone`. */}
          <Route
            path="/geo/expansion-requests/:id"
            element={
              <RequireAuth
                permissions={[
                  'Admin',
                  'Seazone',
                  'SeazoneOnboarding',
                  'SeazoneAdministrative',
                  'SeazoneMarketing',
                  'SeazoneOTA',
                  'SeazonePricing',
                  'SeazoneSysAdmin',
                ]}
              >
                <GeoExpansionRequestDetail />
              </RequireAuth>
            }
          />
          <Route
            path="/channelmanager"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}>
                <ChannelManagerGate>
                  <ChannelManagerHome />
                </ChannelManagerGate>
              </RequireAuth>
            }
          />
          <Route
            path="/channelmanager/propriedades"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}>
                <ChannelManagerGate>
                  <CmPropertyConfig />
                </ChannelManagerGate>
              </RequireAuth>
            }
          />
          <Route
            path="/channelmanager/avaliacoes"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}>
                <ChannelManagerGate>
                  <CmReviews />
                </ChannelManagerGate>
              </RequireAuth>
            }
          />
          <Route
            path="/channelmanager/airbnb"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}>
                <ChannelManagerGate>
                  <AirbnbChannel />
                </ChannelManagerGate>
              </RequireAuth>
            }
          />
          <Route
            path="/channelmanager/airbnb/authorization"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}>
                <ChannelManagerGate>
                  <AirbnbAuthorization />
                </ChannelManagerGate>
              </RequireAuth>
            }
          />
          <Route
            path="/channelmanager/airbnb/inbox"
            element={
              <RequireAuth permissions={['Admin', 'SeazoneOTA', 'SeazoneOnboarding', 'Attendant']}>
                <ChannelManagerGate>
                  <AirbnbInbox />
                </ChannelManagerGate>
              </RequireAuth>
            }
          />
        </Route>

        <Route
          path="/"
          element={
            <OwnerTransferControlProvider>
              <OwnerServicesTermsProvider>
                <OwnerExtractProvider>
                  <OwnerLayout />
                </OwnerExtractProvider>
              </OwnerServicesTermsProvider>
            </OwnerTransferControlProvider>
          }
        >
          <Route
            path="/proprietario"
            element={
              <RequireAuth permissions={['Admin', 'Owner', ...viewModeOwnerPermission]}>
                <Owners />
              </RequireAuth>
            }
          />
          <Route
            path="/proprietario/:id"
            element={
              <RequireAuth permissions={['Admin', 'Owner', ...viewModeOwnerPermission]}>
                <OwnerPropertyDetails />
              </RequireAuth>
            }
          />
          <Route
            path="/meusdados"
            element={
              <RequireAuth permissions={['Admin', 'Owner', ...viewModeOwnerPermission]}>
                <PersonalDataOwnerPage />
              </RequireAuth>
            }
          />
        </Route>
        {/* Entrada do iframe do Programa de Indicação: PÚBLICA de propósito,
            fora do `RequireAuth` de `/parceiros` logo abaixo. Ela existe para
            quem ainda não tem sessão aqui — o guard de `Partner` na frente
            dela seria exatamente o login que a demanda veio remover. Mora sob
            `/parceiros/` porque é o único prefixo que o host serve com
            `frame-ancestors`; em qualquer outro caminho o
            `X-Frame-Options: SAMEORIGIN` impede o iframe de existir. */}
        <Route path="/parceiros/entrada" element={<PartnersSsoEntry />} />
        <Route path="/parceiros/lancamentos" element={<PartnersPublicWrapper />}>
          <Route index element={<PartnersLancamentosPage />} />
          <Route path=":id" element={<PartnersLancamentosDetailPage />} />
        </Route>
        <Route path="/parceiros/revendas" element={<PartnersPublicWrapper />}>
          <Route index element={<PartnersRevendasPage />} />
          <Route path=":id" element={<PartnersRevendasDetailPage />} />
        </Route>
        <Route
          path="/parceiros"
          element={
            <RequireAuth permissions={['Admin', 'Partner']}>
              {/* Atrás de `ff_redirect_hostzone_indicacoes` (indicar e acompanhar
                  indicações) e de `ff_redirect_hostzone_saldo` (Ganhos e
                  Resgatar), as rotas levam ao App Seazone. A tabela de rotas,
                  com a flag de cada linha, mora no próprio componente. */}
              <RedirecionaParaHostzone>
                <Partners />
              </RedirecionaParaHostzone>
            </RequireAuth>
          }
        >
          <Route path="painel" element={<PartnersPanelPage />} />
          <Route path="w/intro" element={<PartnersWelcomePage />} />

          <Route path="indicacoes" element={<PartnersIndicationsPage />}>
            <Route index element={<Navigate to="locacao" replace />} />
            <Route path=":filter" element={<PartnersIndicationsViewsContentPage />} />
            <Route path="locacao/:id" element={<PartnerLocationIndicationDetailPage />} />
          </Route>
          {/* O ternário sobre a flag saiu daqui e virou `PortaoDaJornada`.
              Inline, ele lia a flag sem olhar o `loading` do mesmo hook: o mapa
              nasce todo `false` e um F5 nesta URL redirecionava para o quadro
              ANTES de o PostHog responder, apagando o endereço do histórico
              (`replace`). O portão espera a resposta. */}
          <Route
            path="indicacoes/spot/:id"
            element={
              <PortaoDaJornada>
                <PartnersIndicationDetailPage />
              </PortaoDaJornada>
            }
          />
          {/* Rota própria, e não um passo do stepper da etapa 2: o parceiro
              chega aqui dias depois de gerar o contrato, e precisa de um
              endereço para voltar e terminar o que salvou pela metade. Mesma
              flag do acompanhamento — a rota do servidor também está atrás
              dela, então liberar a tela sem a flag só produziria 404. */}
          <Route
            path="indicacoes/spot/:id/contrato"
            element={
              <PortaoDaJornada>
                <PartnersContractDataPage />
              </PortaoDaJornada>
            }
          />

          {/* Sem `PortaoDaJornada`: a prévia não consulta flag nem servidor, e o
              portão só a faria depender de um PostHog que dev local não tem. */}
          {import.meta.env.DEV && (
            <Route
              path="indicacoes/spot/previa/contrato"
              element={<PartnersContractDataPreviaPage />}
            />
          )}

          <Route
            path="indicar/formulario/spot-lista"
            element={<PartnersBulkSpotIndicationForm />}
          />
          <Route path="indicar" element={<PartnersIndicatePage />}>
            {/* Jornada digital de locação (ff_szs_jornada_unificada_enabled): a
                porta da locação e o wizard checam a flag por dentro; com ela
                desligada `locacao` cai no formulário atual e `nova` mostra o
                aviso com o caminho para ele.

                A home continua sendo o painel (decisão da call de 26/08: a
                landing "Indique, a Seazone fecha" fica para uma conversa com o
                Fernando), então a rota índice só devolve quem cair nela. */}
            <Route index element={<Navigate to="/parceiros/painel" replace />} />
            <Route path="locacao" element={<PartnersLocationEntry />} />
            {/* Fechar indicação de Parceiro (ff_parceiro_fechar): o link do
                convite depois do envio, e o "Gerar novo link" que a lista usa
                para reenviar. A própria tela devolve à lista sem a flag. */}
            <Route path="parceiro/convite/:id" element={<PartnersParceiroConviteEnviado />} />
            {/* `:id?` num único Route: criar a indicação troca a URL de `nova`
                para `nova/:id` sem remontar o wizard (duas rotas irmãs
                remontariam e o banner "Indicação registrada" nunca apareceria). */}
            <Route path="nova/:id?" element={<PartnersLocationWizard />} />
            {/* Escolha da indicação de SPOT: só a indicação ou o contrato do
                início ao fim. A própria tela checa
                `ff_enable_partner_payment_flow` e, sem ela, redireciona para o
                formulário de reserva — o comportamento de hoje. */}
            <Route path={partnersRoutes.indicate.form.spot} element={<PartnersSpotChoice />} />
            {/* `:id?` num único Route, como o wizard da locação em `nova/:id?`:
                criar a indicação troca a URL de `formulario/spot/nova` para
                `formulario/spot/:id` sem remontar o formulário. Duas rotas
                irmãs remontariam, e a etapa 2 perderia o fluxo que acabou de
                semear.

                O segmento É a etapa. Antes ela vivia só no Redux
                (`spotFlowStep`), então um F5 devolvia o formulário VAZIO na
                etapa 1 — e reenviar dali criava uma indicação duplicada da
                mesma unidade. */}
            <Route path="formulario/:type/:id?" element={<PartnersIndicatePageForm />} />
          </Route>

          <Route path="indicar-localizacao" element={<PartnerLocationIndicationForm />} />

          <Route
            path="ganhos"
            element={
              <RequireAccountOwner>
                <PartnersEarningsPage />
              </RequireAccountOwner>
            }
          />
          <Route
            path="resgate"
            element={
              <RequireAccountOwner>
                <PartnersEarningsRescuePage />
              </RequireAccountOwner>
            }
          />
          <Route path="duvidas" element={<PartnersQuestionsPage />} />

          <Route path="links-uteis" element={<PartnersUsefulLinksPage />} />
          <Route path="mapa-terrenos" element={<PartnersMapAllotmentPage />} />
          <Route
            path={partnersRoutes.spotSalesMirror.main}
            element={<PartnersEspelhoVendasPage />}
          />
          <Route
            path="editar-organizacao"
            element={
              <RequireAccountOwner>
                <EditOrganizationPage />
              </RequireAccountOwner>
            }
          />

          <Route
            path={partnersRoutes.spotmatch.main}
            element={
              <RequireAuth permissions={['Partner']}>
                <PartnersSpotmatchListPage />
              </RequireAuth>
            }
          />
          <Route
            path={`${partnersRoutes.spotmatch.main}/${partnersRoutes.spotmatch.new.main}`}
            element={
              <RequireAuth permissions={['Partner']}>
                <PartnersSpotmatchWizardPage />
              </RequireAuth>
            }
          />
          <Route
            path={`${partnersRoutes.spotmatch.main}/${partnersRoutes.spotmatch.edit.main}`}
            element={
              <RequireAuth permissions={['Partner']}>
                <PartnersSpotmatchWizardPage />
              </RequireAuth>
            }
          />
          <Route
            path={`${partnersRoutes.spotmatch.main}/${partnersRoutes.spotmatch.detail.main}`}
            element={
              <RequireAuth permissions={['Partner']}>
                <PartnersSpotmatchDetailPage />
              </RequireAuth>
            }
          />
        </Route>

        <Route path="/precheckin" element={<PreCheckin />}>
          <Route path="" element={<PreCheckinNotFoundPage />} />
          <Route path=":token" element={<PreCheckinMainPage />} />
          <Route path="finalizar" element={<PreCheckinFinishPage />} />
          <Route path="pagina-nao-encontrada" element={<PreCheckinNotFoundPage />} />
          <Route path="token-nao-encontrado" element={<PreCheckinForgotToken />} />
        </Route>

        <Route path="/login" element={<Login />} />
        {/* Rotas do login pelo IDP. Publicas de proposito: o callback fecha o
            fluxo OIDC antes de existir sessao, e a tela de sem-vinculo e
            justamente para quem autenticou e nao pode entrar. */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/sem-conta" element={<SemConta />} />
        <Route path="/recuperar-senha" element={<ForgotPassword />} />
        <Route path="/criarnovasenha" element={<NewPasswordPage />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
